<template>
  <div>
    <div class="flex items-center q-my-sm">
      <h2 class="text-primary text-subtitle2 q-my-none">
        {{ title }}
        <post-count-chip :count="totalUnreviewed" type="unreviewed" size="sm" />
        <post-count-chip :count="totalEscalated" type="escalated" size="sm" />
      </h2>
      <q-space />
      <add-client-btn
        :star="starred"
        title="Add new client"
        round flat
      />
    </div>
    <q-input
      v-model="searchQuery"
      placeholder="Name, email or file ref no."
      autocomplete="no"
      dense filled
    >
      <template v-slot:prepend>
        <q-icon name="fa fa-search" size="xs" color="primary" />
      </template>
    </q-input>
    <div class="flex justify-center q-my-sm">
      <user-client-sort-group
        :order-by="orderBy"
        :order="order"
        flat
        @order-by-changed="orderBy = $event"
        @order-changed="order = $event"/>
    </div>
    <async-items-loader :task="task" :num-items="userClients.length" class="q-mt-md">
      No clients found.
    </async-items-loader>

    <ul class="unstyled q-pa-none">
      <user-client-item
        v-for="userClient in visibleUserClients"
        :key="userClient.id"
        :user-client="userClient"
        class="animated fadeIn"
      />
    </ul>
    <div v-show="canShowMore || showAll" class="text-center">
      <view-all-btn :total="userClients.length" v-model="showAll" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { UserClient, UserClientFilters } from '@/type';
import AsyncItemsLoader from '@/components/item/AsyncItemsLoader.vue';
import eventBus from '@/messaging/event-bus';
import { search } from '@/utils';
import {
  ClientStarred,
  ClientUnstarred,
  PostSaved,
  PostEscalated,
  PostResolved,
  PostTrashed,
  PostRestored,
  PersonCreated,
  PersonMerged,
  PersonTrashed,
  PersonRestored,
  UserAssignedToClient,
  UserUnassignedFromClient,
} from '@/messaging/events';
import { status, isUnreviewed } from '@/social/post/post';
import AddClientBtn from '@/client/create/AddClientBtn.vue';
import UserClientSortGroup from '@/user-client/UserClientSortGroup.vue';
import UserClientItem from './UserClientItem.vue';
import repository from './userClientRepository';
import PostCountChip from './PostCountChip.vue';
import ViewAllBtn from './ViewAllBtn.vue';

interface Data {
  userClients: UserClient[],
  task: Promise<UserClient[]>|null,
  showAll: boolean,
  searchQuery: string,
  orderBy: string,
  order: string,
}

export default defineComponent({
  components: {
    AsyncItemsLoader,
    UserClientItem,
    PostCountChip,
    ViewAllBtn,
    AddClientBtn,
    UserClientSortGroup,
  },
  props: {
    userId: {
      type: Number,
      required: true,
    },
    starred: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      userClients: [],
      task: null,
      showAll: false,
      searchQuery: '',
      orderBy: 'lastName',
      order: 'asc',
    };
  },
  computed: {
    title(): string {
      return this.starred ? 'Starred' : 'Clients';
    },
    activeUserClients(): UserClient[] {
      return this.userClients.filter(
        uc => (uc.client.totalUnreviewed > 0 || uc.client.totalEscalated > 0),
      );
    },
    initialUserClients(): UserClient[] {
      if (this.activeUserClients.length <= 5) return this.userClients;
      return this.activeUserClients;
    },
    filteredUserClients(): UserClient[] {
      const props = [
        'client.firstName', 'client.lastName',
        'client.email', 'client.fileRefNo',
      ];
      const searchQuery = new RegExp(this.searchQuery, 'i');
      return search(this.userClients, props, searchQuery);
    },
    visibleUserClients(): UserClient[] {
      if (this.isSearching) return this.filteredUserClients;
      return this.showAll ? this.userClients : this.initialUserClients;
    },
    isSearching(): boolean {
      return (this.searchQuery !== '');
    },
    canShowMore(): boolean {
      return (
        !this.isSearching
        && this.userClients.length > this.visibleUserClients.length);
    },
    totalUnreviewed(): number {
      const calculator = (total: number, uc: UserClient) => uc.client.totalUnreviewed + total;
      return this.userClients.reduce(calculator, 0);
    },
    totalEscalated(): number {
      const calculator = (total: number, uc: UserClient) => uc.client.totalEscalated + total;
      return this.userClients.reduce(calculator, 0);
    },
    filters(): UserClientFilters {
      return {
        isStarred: this.starred,
        orderBy: this.orderBy,
        order: this.order,
      };
    },
  },
  watch: {
    userId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    filters: {
      handler() {
        this.load();
      },
      deep: true,
    },
  },
  methods: {
    load() {
      this.task = repository.findAll(this.userId, this.filters);
      this.task.then((userClients: UserClient[]) => {
        this.userClients = userClients;
      });
    },
    push(userClient: UserClient) {
      this.userClients.push(userClient);
    },
    remove(userClient: UserClient) {
      const index = this.userClients.findIndex(uc => uc.id === userClient.id);
      if (index >= 0) {
        this.userClients.splice(index, 1);
      }
    },
    onAssign(event: UserAssignedToClient) {
      const { userClient } = event;
      if (userClient.user.id !== this.userId) return;
      if (this.starred && userClient.starredAt !== null) {
        this.push(userClient);
      } else if (!this.starred && userClient.starredAt === null) {
        this.push(userClient);
      }
    },
    onUnassign(event: UserUnassignedFromClient) {
      this.remove(event.userClient);
    },
    onPostSaved(event: PostSaved) {
      const clientId = event.post.client.id;
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === clientId);
      if (userClient) {
        userClient.client.totalUnreviewed -= 1;
      }
    },
    onPostEscalated(event: PostEscalated) {
      const clientId = event.post.client.id;
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === clientId);
      if (userClient) {
        userClient.client.totalEscalated += 1;
        if (isUnreviewed(event.oldPost.status)) {
          userClient.client.totalUnreviewed -= 1;
        }
      }
    },
    onPostResolved(event: PostResolved) {
      const clientId = event.post.client.id;
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === clientId);
      if (userClient) {
        userClient.client.totalEscalated -= 1;
      }
    },
    onPostTrashed(event: PostTrashed) {
      const { post } = event;
      const clientId = post.client.id;
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === clientId);
      if (!userClient) return;
      if (post.status === status.UNREVIEWED) {
        userClient.client.totalUnreviewed -= 1;
      }
      if (post.status === status.ESCALATED) {
        userClient.client.totalEscalated -= 1;
      }
    },
    onPostRestored(event: PostRestored) {
      const { post } = event;
      const clientId = post.client.id;
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === clientId);
      if (!userClient) return;
      if (post.status === status.UNREVIEWED) {
        userClient.client.totalUnreviewed += 1;
      }
      if (post.status === status.ESCALATED) {
        userClient.client.totalEscalated += 1;
      }
    },
    onPersonCreated(event: PersonCreated) {
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === event.clientId);
      if (userClient) {
        userClient.client.totalPersons += 1;
      }
    },
    onPersonMerged(event: PersonMerged) {
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === event.clientId);
      if (userClient) {
        userClient.client.totalPersons -= event.sourcePersonIds.length;
      }
    },
    onPersonTrashed(event: PersonTrashed) {
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === event.clientId);
      if (userClient) {
        userClient.client.totalPersons -= 1;
        userClient.client.totalTrashedPersons += 1;
      }
    },
    onPersonRestored(event: PersonRestored) {
      const userClient = this.userClients.find((uc: UserClient) => uc.client.id === event.clientId);
      if (userClient) {
        userClient.client.totalPersons += 1;
        userClient.client.totalTrashedPersons -= 1;
      }
    },
  },
  created(): void {
    if (this.starred) {
      eventBus.$on(ClientStarred.eventName, this.push);
      eventBus.$on(ClientUnstarred.eventName, this.remove);
    } else {
      eventBus.$on(ClientStarred.eventName, this.remove);
      eventBus.$on(ClientUnstarred.eventName, this.push);
    }
    eventBus.$on(UserAssignedToClient.eventName, this.onAssign);
    eventBus.$on(UserUnassignedFromClient.eventName, this.onUnassign);

    eventBus.$on(PostSaved.eventName, this.onPostSaved);
    eventBus.$on(PostEscalated.eventName, this.onPostEscalated);
    eventBus.$on(PostResolved.eventName, this.onPostResolved);
    eventBus.$on(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$on(PostRestored.eventName, this.onPostRestored);

    eventBus.$on(PersonCreated.eventName, this.onPersonCreated);
    eventBus.$on(PersonMerged.eventName, this.onPersonMerged);
    eventBus.$on(PersonTrashed.eventName, this.onPersonTrashed);
    eventBus.$on(PersonRestored.eventName, this.onPersonRestored);
  },
  beforeUnmount(): void {
    if (this.starred) {
      eventBus.$off(ClientStarred.eventName, this.push);
      eventBus.$off(ClientUnstarred.eventName, this.remove);
    } else {
      eventBus.$off(ClientStarred.eventName, this.remove);
      eventBus.$off(ClientUnstarred.eventName, this.push);
    }
    eventBus.$off(UserAssignedToClient.eventName, this.onAssign);
    eventBus.$off(UserUnassignedFromClient.eventName, this.onUnassign);

    eventBus.$off(PostSaved.eventName, this.onPostSaved);
    eventBus.$off(PostEscalated.eventName, this.onPostEscalated);
    eventBus.$off(PostResolved.eventName, this.onPostResolved);
    eventBus.$off(PostTrashed.eventName, this.onPostTrashed);
    eventBus.$off(PostRestored.eventName, this.onPostRestored);

    eventBus.$off(PersonCreated.eventName, this.onPersonCreated);
    eventBus.$off(PersonMerged.eventName, this.onPersonMerged);
    eventBus.$off(PersonTrashed.eventName, this.onPersonTrashed);
    eventBus.$off(PersonRestored.eventName, this.onPersonRestored);
  },
});
</script>
